import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
    minHeight: 280,
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(0.5),
    boxSizing: 'border-box',
  },
  rootCompact: {
    minHeight: 200,
    paddingTop: theme.spacing(2),
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: 'rgba(15, 23, 42, 0.04)',
    border: '1px solid rgba(15, 23, 42, 0.06)',
  },
  bar: {
    height: 12,
    borderRadius: 6,
    backgroundColor: 'rgba(15, 23, 42, 0.1)',
  },
  barWide: {
    width: '55%',
    height: 14,
    borderRadius: 6,
    backgroundColor: 'rgba(15, 23, 42, 0.12)',
  },
  barFull: {
    width: '100%',
  },
  barMedium: {
    width: '88%',
  },
  barShort: {
    width: '42%',
  },
  media: {
    marginTop: theme.spacing(0.5),
    width: '100%',
    maxWidth: 360,
    height: 120,
    borderRadius: 8,
    backgroundColor: 'rgba(15, 23, 42, 0.08)',
    alignSelf: 'flex-start',
  },

  blockDivider: {
    borderTop: '1px solid rgba(15, 23, 42, 0.1)',
    width: '100%',
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  fileActionRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 10,
    marginTop: theme.spacing(0.5),
  },
  fileActionButton: {
    height: 36,
    minWidth: 148,
    maxWidth: '48%',
    flex: '1 1 auto',
    borderRadius: 4,
    backgroundColor: 'rgba(15, 23, 42, 0.11)',
  },
}));

/**
 * Placeholder skeleton for stacked content blocks (e.g. manual subparagraphs).
 * Keeps layout stable while async content loads.
 *
 * showFileActionRow: two placeholder “buttons” like “Aggiungi immagine PNG” /
 * “Aggiungi documento PDF” at the bottom of each card (text blocks only in
 * the real UI; table-only paragraphs may reserve a little extra space).
 */
export default function ContentBlocksSkeleton({
  'aria-label': ariaLabel,
  cardCount,
  className,
  compact,
  showMediaOnFirstCard,
  showFileActionRow,
}) {
  const classes = useStyles();
  const rootClass = `${classes.root} ${compact ? classes.rootCompact : ''} ${
    className || ''
  }`.trim();

  const indices = Array.from({ length: cardCount }, (_, i) => i);

  return (
    <div
      className={rootClass}
      aria-busy="true"
      aria-label={ariaLabel}
      role="status"
    >
      {indices.map((i) => (
        <div key={i} className={classes.card}>
          <div className={`${classes.bar} ${classes.barWide}`} />
          <div className={`${classes.bar} ${classes.barFull}`} />
          <div className={`${classes.bar} ${classes.barFull}`} />
          <div className={`${classes.bar} ${classes.barFull}`} />
          <div className={`${classes.bar} ${classes.barMedium}`} />
          <div className={`${classes.bar} ${classes.barFull}`} />
          <div className={`${classes.bar} ${classes.barFull}`} />
          <div className={`${classes.bar} ${classes.barFull}`} />
          <div className={`${classes.bar} ${classes.barMedium}`} />
          <div className={`${classes.bar} ${classes.barFull}`} />
          <div className={`${classes.bar} ${classes.barFull}`} />
          <div className={`${classes.bar} ${classes.barFull}`} />
          <div className={`${classes.bar} ${classes.barShort}`} />
          {showMediaOnFirstCard && i === 0 && <div className={classes.media} />}
          {showFileActionRow && (
            <div className={classes.blockDivider}>
              <div className={classes.fileActionRow}>
                <div className={classes.fileActionButton} />
                <div className={classes.fileActionButton} />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

ContentBlocksSkeleton.propTypes = {
  'aria-label': PropTypes.string,
  cardCount: PropTypes.number,
  className: PropTypes.string,
  compact: PropTypes.bool,
  showMediaOnFirstCard: PropTypes.bool,
  showFileActionRow: PropTypes.bool,
};

ContentBlocksSkeleton.defaultProps = {
  'aria-label': 'Caricamento contenuto',
  cardCount: 1,
  className: '',
  compact: false,
  showMediaOnFirstCard: true,
  showFileActionRow: false,
};
